import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { AutomationBatchTargetListEntity } from './automation-batch-target-list.entity';
import { AutomationFlowEntity } from './automation-flow.entity';
import { AutomationVersionEntity } from './automation-version.entity';
import { BaseEntity } from './base.entity';

export type AutomationBatchStatus =
	| 'pending'
	| 'scheduled'
	| 'preparing'
	| 'running'
	| 'completed'
	| 'failed'
	| 'cancelled'
	| 'recurring';

export interface AutomationBatchRecurringConfig {
	type: 'cron' | 'interval';
	cronExpression?: string;
	intervalMinutes?: number;
	timezone?: string;
	allowConcurrentRuns?: boolean;
}

@Entity('_automation_batch')
export class AutomationBatchEntity extends BaseEntity {
	@Column({ name: '_name', type: 'varchar', length: 255 })
	public name!: string;

	@Column({ name: '_description', type: 'text', nullable: true })
	public description?: string;

	@Column({
		name: '_fk__automation_flow__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public flowId?: number;

	@Column({
		name: '_fk__automation_version__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'Locked version during execution',
	})
	public versionId?: number;

	@Column({
		name: '_batch_status',
		type: 'enum',
		enum: [
			'pending',
			'scheduled',
			'preparing',
			'running',
			'completed',
			'failed',
			'cancelled',
			'recurring',
		],
		default: "'pending'",
	})
	public batchStatus!: AutomationBatchStatus;

	@Column({
		name: '_total_count',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public totalCount!: number;

	@Column({
		name: '_completed_count',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public completedCount!: number;

	@Column({
		name: '_failed_count',
		type: 'int',
		unsigned: true,
		default: 0,
	})
	public failedCount!: number;

	@Column({
		name: '_started_at',
		type: 'timestamp',
		nullable: true,
	})
	public startedAt?: Date;

	@Column({
		name: '_completed_at',
		type: 'timestamp',
		nullable: true,
	})
	public completedAt?: Date;

	@Column({
		name: '_scheduled_for',
		type: 'timestamp',
		nullable: true,
		comment: 'When this batch should be processed',
	})
	public scheduledFor?: Date;

	// Configuration for the batch (e.g., list filters, entity criteria)
	@Column({ name: '_batch_config', type: 'json', nullable: true })
	public batchConfig?: Record<string, any>;

	// Metadata about the batch execution
	@Column({ name: '_metadata', type: 'json', nullable: true })
	public metadata?: Record<string, any>;

	// Queue ownership for isolation between systems
	@Column({ name: '_queue_name', type: 'varchar', length: 255, nullable: true })
	public queueName?: string;

	// === Recurring Batch Configuration ===
	@Column({ name: '_is_recurring', type: 'boolean', default: false })
	public isRecurring!: boolean;

	@Column({ name: '_is_active', type: 'boolean', default: true })
	public isActive!: boolean;

	@Column({ name: '_recurring_config', type: 'json', nullable: true })
	public recurringConfig?: AutomationBatchRecurringConfig;

	@Column({ name: '_next_run_at', type: 'timestamp', nullable: true })
	public nextRunAt?: Date;

	@Column({ name: '_last_run_at', type: 'timestamp', nullable: true })
	public lastRunAt?: Date;

	@Column({ name: '_run_count', type: 'int', unsigned: true, default: 0 })
	public runCount!: number;

	/**
	 * Automation variable names to use for de-duplication.
	 * When processing sources, entities with matching values for these fields
	 * will be considered duplicates and only the first occurrence is processed.
	 * Example: ['recipientEmail'] - de-duplicates on the mapped email address.
	 */
	@Column({ name: '_deduplication_fields', type: 'json', nullable: true })
	public deduplicationFields?: Array<string>;

	// === Parent-Child Relationship ===
	@Column({
		name: '_fk__automation_batch__id_parent',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public parentBatchId?: number;

	@ManyToOne(() => AutomationFlowEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_flow__id' })
	public flow?: AutomationFlowEntity;

	@ManyToOne(() => AutomationVersionEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_version__id' })
	public version?: AutomationVersionEntity;

	@OneToMany(() => AutomationBatchTargetListEntity, (btl) => btl.batch)
	public targetLists!: Array<AutomationBatchTargetListEntity>;

	@ManyToOne(() => AutomationBatchEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_batch__id_parent' })
	public parentBatch?: AutomationBatchEntity;

	@OneToMany(() => AutomationBatchEntity, (batch) => batch.parentBatch)
	public childBatches?: Array<AutomationBatchEntity>;

	@Index('idx_automation_batch_flow')
	public static flowIndex: [string] = ['flowId'];

	@Index('idx_automation_batch_version')
	public static versionIndex: [string] = ['versionId'];

	@Index('idx_automation_batch_status')
	public static statusIndex: [string] = ['batchStatus'];

	@Index('idx_automation_batch_scheduled')
	public static scheduledIndex: [string] = ['scheduledFor'];

	@Index('idx_automation_batch_status_queue')
	public static statusQueueIndex: [string, string] = [
		'batchStatus',
		'queueName',
	];

	@Index('idx_automation_batch_scheduled_queue')
	public static scheduledQueueIndex: [string, string] = [
		'scheduledFor',
		'queueName',
	];

	@Index('idx_automation_batch_recurring')
	public static recurringIndex: [string, string, string] = [
		'batchStatus',
		'isActive',
		'nextRunAt',
	];

	@Index('idx_automation_batch_parent')
	public static parentIndex: [string] = ['parentBatchId'];
}
